import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import SnackBar from "../../components/SnackBar";
import { reprocessarAnosApuracao } from "../../services/apuracao.service";
import { configRelatorioApuracao } from "./configRelatorioApuracao.util";
import { anosNoPeriodo, exibirCodRelatorio, fmtMesAno } from "./utils";
import { formatValue } from "../../utils/formatValue";
import styles from "./styles.module.css";

/**
 * TXM-325 item 08 — Reprocessar anos de uma apuração (Sucesso, Divergência ou Erro).
 * Esvazia (TRUNCATE) a base do(s) ano(s) marcado(s) e regenera — com os termos de
 * exclusão herdados da apuração (sem nova opção de exclusão aqui) — atualizados com
 * Selic ao final; anos não marcados ficam intactos.
 */
const ModalReprocessarAnos = ({ open, onClose, onSuccess, onError, apuracao = null, idApuracao }) => {
  const [anosSelecionados, setAnosSelecionados] = useState([]);
  const [contasCriterio, setContasCriterio] = useState("resultado");
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const anosDisponiveis = anosNoPeriodo(apuracao?.periodoInicio, apuracao?.periodoFim);

  useEffect(() => {
    if (!open) {
      setObjSnackBar({ type: "", msg: "" });
      return;
    }
    setAnosSelecionados([]);
    // TXM-325 — 95% dos casos são só contas de resultado; default "resultado" a cada abertura.
    setContasCriterio("resultado");
  }, [open, apuracao]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const toggleAno = (ano) => {
    setAnosSelecionados((prev) =>
      prev.includes(ano) ? prev.filter((a) => a !== ano) : [...prev, ano]
    );
  };

  const exibeContasCriterio = configRelatorioApuracao(apuracao?.tipo).campos
    .contasCriterio;

  const handleConfirmar = async () => {
    if (!idApuracao || anosSelecionados.length === 0) return;
    setLoading(true);
    try {
      // API ainda exige contas_criterio; no eSocial o campo é inerte — envia default.
      const res = await reprocessarAnosApuracao(idApuracao, {
        anos: anosSelecionados,
        contas_criterio: exibeContasCriterio ? contasCriterio : "resultado",
      });
      if (res?.sucesso) {
        onSuccess?.(res.mensagem || "Reprocessamento enfileirado.");
        handleClose();
      } else {
        const msg = res?.mensagem || "Não foi possível reprocessar os anos selecionados.";
        onError?.(msg);
        setObjSnackBar({ type: "error", msg });
      }
    } catch (e) {
      const msg = e?.message || "Erro ao solicitar reprocessamento por ano.";
      onError?.(msg);
      setObjSnackBar({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  const cnpjFmt = formatValue("cnpj", apuracao?.cnpj);
  const empresa = apuracao?.empresa || "—";
  const tipo = exibirCodRelatorio(apuracao?.tipo);
  const periodoLabel = `${fmtMesAno(apuracao?.periodoInicio)} – ${fmtMesAno(apuracao?.periodoFim)}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="modal-reprocessar-titulo"
      >
        <DialogTitle id="modal-reprocessar-titulo">
          <div className={styles.title}>
            <ReplayIcon aria-hidden sx={{ marginRight: ".5rem" }} />
            Reprocessar
          </div>
        </DialogTitle>

        <DialogContent className={styles.reprocessarContent}>
          <section className={styles.reprocessarContexto} aria-label="Dados da apuração">
            <header className={styles.reprocessarContextoHeader}>
              <p className={styles.reprocessarEmpresa}>{empresa}</p>
              <p className={styles.reprocessarCnpj}>{cnpjFmt || "—"}</p>
            </header>
            <dl className={styles.reprocessarMeta}>
              <div className={styles.reprocessarMetaItem}>
                <dt>Relatório</dt>
                <dd>{tipo || "—"}</dd>
              </div>
              <div className={styles.reprocessarMetaItem}>
                <dt>Período</dt>
                <dd>{periodoLabel}</dd>
              </div>
            </dl>
          </section>

          <section className={styles.reprocessarSecao} aria-labelledby="reprocessar-anos-titulo">
            <h3 id="reprocessar-anos-titulo" className={styles.reprocessarSecaoTitulo}>
              Anos
            </h3>
            <fieldset className={styles.reprocessarFieldset} disabled={loading}>
              <legend className={styles.srOnly}>Selecione os anos a reprocessar</legend>
              {anosDisponiveis.length === 0 ? (
                <p className={styles.reprocessarVazio}>
                  Nenhum ano identificado no período desta apuração.
                </p>
              ) : (
                <div className={styles.reprocessarAnosGrid} role="group">
                  {anosDisponiveis.map((ano) => (
                    <FormControlLabel
                      key={ano}
                      className={styles.reprocessarAnoItem}
                      control={
                        <Checkbox
                          checked={anosSelecionados.includes(ano)}
                          onChange={() => toggleAno(ano)}
                          size="small"
                        />
                      }
                      label={String(ano)}
                    />
                  ))}
                </div>
              )}
            </fieldset>
          </section>

          {exibeContasCriterio && (
            <section className={styles.reprocessarSecao} aria-labelledby="reprocessar-escopo-titulo">
              <h3 id="reprocessar-escopo-titulo" className={styles.reprocessarSecaoTitulo}>
                Escopo
              </h3>
              <FormControlLabel
                className={styles.reprocessarSwitch}
                control={
                  <Switch
                    checked={contasCriterio === "resultado"}
                    onChange={(e) =>
                      setContasCriterio(e.target.checked ? "resultado" : "todas")
                    }
                    disabled={loading}
                  />
                }
                label="Apenas contas de resultado"
              />
            </section>
          )}
        </DialogContent>

        <DialogActions className={styles.containerBtn}>
          <Button
            onClick={handleClose}
            variant="contained"
            className={styles.btnCancelar}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            variant="contained"
            className={styles.btnSalvar}
            disabled={loading || anosSelecionados.length === 0}
          >
            {loading ? "Enfileirando…" : "Reprocessar"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalReprocessarAnos;
